import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Slider } from '../../../components/ui/slider';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

export default function TwinPreferences() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [responseLength, setResponseLength] = useState('medium');
  const [formalityLevel, setFormalityLevel] = useState(5);
  const [boundaries, setBoundaries] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/personal/settings');
      const data = response.data;
      setResponseLength(data.responseLength || 'medium');
      setFormalityLevel(data.formalityLevel || 5);
      setBoundaries(data.boundaries?.join(', ') || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await apiClient.post('/personal/settings', {
        responseLength,
        formalityLevel,
        boundaries: boundaries.split(',').map(b => b.trim()).filter(Boolean),
        notificationSchedule: {},
        dataSources: {},
      });
      toast.success(t('success.updated'));
      navigate('/personal/twin');
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="twin-preferences-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personal/twin')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">{t('twin.preferences')}</h1>
          <p className="text-gray-600 mt-1">{t('twin.configureComms')}</p>
        </div>
      </div>

      <Card className="p-6 rounded-2xl space-y-6">
        <div>
          <Label htmlFor="responseLength">{t('twin.responseLength')}</Label>
          <select
            id="responseLength"
            value={responseLength}
            onChange={(e) => setResponseLength(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30"
          >
            <option value="short">{t('twin.short')}</option>
            <option value="medium">{t('twin.medium')}</option>
            <option value="long">{t('twin.long')}</option>
          </select>
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <Label>{t('twin.formalityLevel')}</Label>
            <span className="text-sm text-gray-500">{formalityLevel}/10</span>
          </div>
          <Slider
            value={[formalityLevel]}
            onValueChange={([value]) => setFormalityLevel(value)}
            min={1}
            max={10}
            step={1}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">Very casual</span>
            <span className="text-xs text-gray-500">Very formal</span>
          </div>
        </div>

        <div>
          <Label htmlFor="boundaries">{t('twin.conversationBoundaries')}</Label>
          <Textarea
            id="boundaries"
            value={boundaries}
            onChange={(e) => setBoundaries(e.target.value)}
            placeholder={t('twin.topicsToAvoid')}
            className="mt-1"
            rows={4}
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter topics your twin should avoid discussing
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/personal/twin')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? t('skills.saving') : t('common.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
